import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@pantry/ui';
import { useSetStockTarget, useSetItemStockTarget } from '@pantry/api-client';
import { ITEM_UNITS, UNIT_DISPLAY_LABELS, type ItemUnit } from '@pantry/shared';
import { toast } from 'sonner';

interface SetTargetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'group' | 'item';
  entityId: string;
  entityName: string;
  householdId: string;
  currentQuantity?: number | null;
  currentUnit?: string | null;
}

/**
 * Dialog for setting or removing a stock target on a product group or individual item.
 */
export function SetTargetDialog({
  open,
  onOpenChange,
  mode,
  entityId,
  entityName,
  householdId,
  currentQuantity,
  currentUnit,
}: SetTargetDialogProps) {
  const { t } = useLingui();
  const [quantity, setQuantity] = useState<string>(
    currentQuantity != null ? String(currentQuantity) : ''
  );
  const [unit, setUnit] = useState<ItemUnit>((currentUnit as ItemUnit) ?? 'item');
  const [error, setError] = useState<string | null>(null);

  const setGroupTarget = useSetStockTarget();
  const setItemTarget = useSetItemStockTarget();

  const mutation = mode === 'group' ? setGroupTarget : setItemTarget;

  useEffect(() => {
    if (open) {
      setQuantity(currentQuantity != null ? String(currentQuantity) : '');
      setUnit((currentUnit as ItemUnit) ?? 'item');
      setError(null);
    }
  }, [open, currentQuantity, currentUnit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsedQuantity = quantity.trim() === '' ? null : Number(quantity);

    if (parsedQuantity !== null && (isNaN(parsedQuantity) || parsedQuantity <= 0)) {
      setError(t`Please enter a valid quantity greater than 0`);
      return;
    }

    try {
      const data = {
        targetQuantity: parsedQuantity,
        targetUnit: parsedQuantity != null ? unit : null,
      };

      if (mode === 'group') {
        await setGroupTarget.mutateAsync({ householdId, groupId: entityId, data });
      } else {
        await setItemTarget.mutateAsync({ householdId, itemId: entityId, data });
      }

      toast.success(t`Stock target updated for "${entityName}"`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to update stock target`;
      setError(message);
    }
  };

  const handleRemoveTarget = async () => {
    setError(null);

    try {
      const data = { targetQuantity: null, targetUnit: null };

      if (mode === 'group') {
        await setGroupTarget.mutateAsync({ householdId, groupId: entityId, data });
      } else {
        await setItemTarget.mutateAsync({ householdId, itemId: entityId, data });
      }

      toast.success(t`Stock target removed for "${entityName}"`);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to remove stock target`;
      setError(message);
    }
  };

  const hasExistingTarget = currentQuantity != null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="font-display">
            <Trans context="dialog_title">Set Stock Target</Trans>
          </DialogTitle>
          <DialogDescription className="pt-1">
            <Trans>
              Set a minimum stock target for <strong>{entityName}</strong>. You&apos;ll be notified
              when stock falls below this level.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1 space-y-2">
              <Label htmlFor="target-quantity">
                <Trans>Target quantity</Trans>
              </Label>
              <Input
                id="target-quantity"
                type="number"
                min="0.01"
                step="any"
                placeholder={t`e.g. 2`}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="rounded-xl"
                autoFocus
              />
            </div>

            <div className="w-28 space-y-2">
              <Label htmlFor="target-unit">
                <Trans>Unit</Trans>
              </Label>
              <select
                id="target-unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value as ItemUnit)}
                className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
              >
                {ITEM_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {UNIT_DISPLAY_LABELS[u]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:gap-0">
            {hasExistingTarget && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleRemoveTarget}
                disabled={mutation.isPending}
                className="rounded-xl text-destructive hover:text-destructive hover:bg-destructive/10 sm:mr-auto"
              >
                <Trans context="action_button">Remove Target</Trans>
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
              className="rounded-xl"
            >
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button type="submit" disabled={mutation.isPending} className="rounded-xl">
              {mutation.isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                  <Trans>Saving...</Trans>
                </div>
              ) : (
                <Trans context="action_button">Save Target</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
